import type React from 'react'
import type { FieldCoordinateSystem } from '@/utils'
import type { AlignmentSegment } from '@/utils/Alignment.utils'
import { PLAYER_RADIUS_FEET } from '@/constants'
import './alignment-guidelines-overlay.css'

interface AlignmentGuidelinesOverlayProps {
	segments: AlignmentSegment[]
	coordSystem: FieldCoordinateSystem
}

/**
 * Renders visual alignment guidelines as dashed lines with distance labels.
 *
 * Guidelines appear when players are aligned on the same X or Y grid coordinate.
 * Shows segmented lines between adjacent aligned players, with distance labels
 * only where there's sufficient space.
 */
export function AlignmentGuidelinesOverlay({
											   segments,
											   coordSystem,
										   }: AlignmentGuidelinesOverlayProps) {
	if (!segments || segments.length === 0) {
		return null
	}

	return (
		<g className="alignment-guidelines-overlay" pointerEvents="none">
			{segments.map((segment) => (
				<AlignmentSegmentLine
					key={`${segment.startPlayer.playerId}-${segment.endPlayer.playerId}-${segment.type}`}
					segment={segment}
					coordSystem={coordSystem}
				/>
			))}
		</g>
	)
}

interface AlignmentSegmentLineProps {
	segment: AlignmentSegment
	coordSystem: FieldCoordinateSystem
}

function AlignmentSegmentLine({ segment, coordSystem }: AlignmentSegmentLineProps) {
	// Adjust start and end positions to be at player edges, not centers
	// This prevents guidelines from showing through player circles
	// Special handling: LOS segments have one endpoint with playerId='los' that shouldn't be adjusted
	let startX = segment.startPlayer.position.x
	let startY = segment.startPlayer.position.y
	let endX = segment.endPlayer.position.x
	let endY = segment.endPlayer.position.y

	const isStartLOS = segment.startPlayer.playerId === 'los'
	const isEndLOS = segment.endPlayer.playerId === 'los'

	if (segment.type === 'horizontal') {
		// Horizontal line: adjust X coordinates for players only
		if (startX < endX) {
			// Start is to the left
			if (!isStartLOS) startX += PLAYER_RADIUS_FEET
			if (!isEndLOS) endX -= PLAYER_RADIUS_FEET
		} else {
			// Start is to the right
			if (!isStartLOS) startX -= PLAYER_RADIUS_FEET
			if (!isEndLOS) endX += PLAYER_RADIUS_FEET
		}
	} else {
		// Vertical line: adjust Y coordinates for players only
		if (startY < endY) {
			// Start is below (lower Y value)
			if (!isStartLOS) startY += PLAYER_RADIUS_FEET
			if (!isEndLOS) endY -= PLAYER_RADIUS_FEET
		} else {
			// Start is above (higher Y value)
			if (!isStartLOS) startY -= PLAYER_RADIUS_FEET
			if (!isEndLOS) endY += PLAYER_RADIUS_FEET
		}
	}

	// Convert adjusted feet coordinates to pixel coordinates
	const startPixels = coordSystem.feetToPixels(startX, startY)
	const endPixels = coordSystem.feetToPixels(endX, endY)

	// Calculate midpoint for distance label
	const midX = (startPixels.x + endPixels.x) / 2
	const midY = (startPixels.y + endPixels.y) / 2

	return (
		<g>
			{/* Alignment line */}
			<line
				x1={startPixels.x}
				y1={startPixels.y}
				x2={endPixels.x}
				y2={endPixels.y}
				className="alignment-guideline-line"
			/>

			{/* Distance label - only show if there's enough space */}
			{segment.showLabel && (
				<DistanceLabel x={midX} y={midY} distance={segment.distanceFeet} />
			)}
		</g>
	)
}

interface DistanceLabelProps {
	x: number
	y: number
	distance: number
}

function DistanceLabel({ x, y, distance }: DistanceLabelProps) {
	const text = `${Math.round(distance)}ft`

	// Approximate text dimensions for pill background
	// Font size is 11px, approximate width is 6px per character
	const textWidth = text.length * 6
	const textHeight = 11

	const paddingX = 6
	const paddingY = 3

	const rectWidth = textWidth + paddingX * 2
	const rectHeight = textHeight + paddingY * 2

	return (
		<g transform={`translate(${x}, ${y})`}>
			{/* Background pill */}
			<rect
				className="alignment-label-background"
				x={-rectWidth / 2}
				y={-rectHeight / 2}
				width={rectWidth}
				height={rectHeight}
				rx={4}
			/>

			{/* Distance text */}
			<text
				className="alignment-label-text"
				textAnchor="middle"
				dominantBaseline="middle"
			>
				{text}
			</text>
		</g>
	)
}
